//physicalinventory
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import Layout from "../../Component/Layout";
import Select from "react-select";
import { PURCHASE_API, MASTER_API } from "../../apiConfig";
import { Link, useLocation } from "react-router-dom";
import { FileText } from 'lucide-react';
import { authHeaders } from "../../utils/authHeaders";

const styles = {
    pageTitleBar: {
        marginBottom: "20px"
    },

    pageTitle: {
        fontSize: "28px",
        fontWeight: "600",
        color: "#0f172a",
        marginBottom: "4px"
    },

    pageSubtitle: {
        color: "#64748b",
        fontSize: "14px"
    },

    filterCard: {
        background: "#fff",
        borderRadius: "8px",
        padding: "15px",
        boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
        marginBottom: "15px"
    },

    compactLabel: {
        fontSize: "12px",
        fontWeight: "600",
        color: "#334155",
        marginBottom: "4px",
        display: "block"
    },

    dateInput: {
        width: "100%",
        height: "38px",
        padding: "6px 10px",
        fontSize: "13px",
        color: "#0f172a",
        border: "1px solid #cbd5e1",
        borderRadius: "6px",
        boxSizing: "border-box"
    },

    exportBtn: {
        background: "#16a34a",
        color: "#fff",
        border: "none",
        borderRadius: "6px",
        padding: "8px 16px",
        fontSize: "13px",
        fontWeight: "600",
        cursor: "pointer"
    },

    exportBtnDisabled: {
        background: "#94a3b8",
        color: "#fff",
        border: "none",
        borderRadius: "6px",
        padding: "8px 16px",
        fontSize: "13px",
        fontWeight: "600",
        cursor: "not-allowed"
    },

    tableContainer: {
        background: "#fff",
        borderRadius: "12px",
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        padding: "10px",
        border: "1px solid #e2e8f0",
        borderBottom: "none",
        overflowX: "auto"
    },

    erpTable: {
        marginBottom: 0,
        fontSize: "13px"
    },

    erpTh: {
        background: "#ffffff",
        color: "#0f172a",
        fontWeight: "600",
        padding: "12px",
        borderBottom: "1px solid #e2e8f0",
        whiteSpace: "nowrap"
    },

    erpTd: {
        padding: "10px 12px",
        borderBottom: "1px solid #f1f5f9",
        whiteSpace: "nowrap",
        color: "#334155"
    },

    cellLink: {
        color: "#2563eb",
        textDecoration: "none",
        fontWeight: 600,
        cursor: "pointer"
    },

    paginationBar: {
        background: "#fff",
        borderRadius: "12px",
        padding: "14px 20px",
        border: "1px solid #e2e8f0",
        borderTop: "none",
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between"
    },

    paginationInfo: {
        fontSize: "13px",
        color: "#334155"
    },

    paginationButtons: {
        display: "flex",
        gap: "8px"
    },

    paginationBtn: {
        padding: "6px 16px",
        fontSize: "13px",
        fontWeight: "600",
        borderRadius: "6px",
        border: "1px solid #e2e8f0",
        background: "#fff",
        color: "#334155",
        cursor: "pointer"
    },

    paginationBtnDisabled: {
        padding: "6px 16px",
        fontSize: "13px",
        fontWeight: "600",
        borderRadius: "6px",
        border: "1px solid #e2e8f0",
        background: "#f8fafc",
        color: "#cbd5e1",
        cursor: "not-allowed"
    },

    pageSizeSelect: {
        padding: "6px 10px",
        fontSize: "13px",
        fontWeight: "600",
        borderRadius: "6px",
        border: "1px solid #e2e8f0",
        background: "#fff",
        color: "#334155",
        cursor: "pointer",
        outline: "none"
    }
};

const modalStyles = {
    overlay: { position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 },
    modal: { background: "#fff", borderRadius: 12, width: "100%", maxWidth: 1300, maxHeight: "92vh", display: "flex", flexDirection: "column", boxShadow: "0 10px 40px rgba(0,0,0,0.25)" },
    header: { padding: "16px 20px", borderBottom: "1px solid #e2e8f0" },
    summary: { display: "flex", gap: 10, flexWrap: "wrap", padding: "12px 20px", borderBottom: "1px solid #f1f5f9" },
    chip: { background: "#f1f5f9", color: "#334155", borderRadius: 999, padding: "4px 12px", fontSize: 12, fontWeight: 600 },
    body: { overflow: "auto", padding: "0 20px", flex: 1 },
    th: { position: "sticky", top: 0, background: "#fff", padding: "10px 10px", fontSize: 12, fontWeight: 600, color: "#0f172a", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap", zIndex: 1 },
    td: { padding: "8px 10px", fontSize: 12, color: "#334155", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" },
    footer: { padding: "14px 20px", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }
};

// Strips unnecessary trailing decimal zeros from a qty value coming back
// from the DB as a fixed-precision string (e.g. "100.00" -> "100",
// "25.50" -> "25.5"). Leaves non-numeric / empty values as-is.
const formatQty = (qty) => {
    if (qty === null || qty === undefined || qty === "") return qty;
    const num = Number(qty);
    if (Number.isNaN(num)) return qty;
    return num.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

// Renders a datetime (e.g. "2026-09-02T10:15:00.000Z") as "02-09-2026" for
// display (dd-mm-yyyy, numeric month). Leaves null/invalid values as "-".
const formatDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "-";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
};

const PAGE_SIZE_OPTIONS = [10, 15, 20, 25, 30, 40, 50];

// ── Type-specific attribute filters (mirrors Product.js) ──
// Raw Material  -> Category, Sub Category, Quality Category
// Anything else (or no type selected) -> cable-style attribute filters
const FINISHED_FILTERS = [
    { key: "category", label: "Category" },
    { key: "cableType", label: "Cable Type" },
    { key: "code", label: "Code" },
    { key: "color", label: "Color" },
    { key: "noOfCores", label: "Cores" },
    { key: "nominalCrossSection", label: "Cross Section" },
    { key: "referenceOfStandard", label: "Std. Ref" },
    { key: "voltage", label: "Voltage" },
    { key: "construction", label: "Construction" },
];

const RAW_MATERIAL_FILTERS = [
    { key: "category", label: "Category" },
    { key: "subCategory", label: "Sub Category" },
    { key: "qualityCategory", label: "Quality Category" },
];

// ── Styles for the cable-attribute filter row (matches Production Order) ──
const cableFilterSelectStyles = {
    control: (base, state) => ({ ...base, minHeight: 32, height: 32, fontSize: 12, borderColor: state.isFocused ? "#64748b" : "#e2e8f0", borderRadius: 6, boxShadow: "none", cursor: "pointer", "&:hover": { borderColor: "#94a3b8" } }),
    valueContainer: (base) => ({ ...base, padding: "0 8px", height: 32, flexWrap: "nowrap" }),
    input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: 12 }),
    indicatorsContainer: (base) => ({ ...base, height: 32 }),
    dropdownIndicator: (base) => ({ ...base, padding: "0 4px", color: "#94a3b8", svg: { width: 12, height: 12 } }),
    clearIndicator: (base) => ({ ...base, padding: "0 2px", color: "#94a3b8", svg: { width: 12, height: 12 } }),
    indicatorSeparator: () => ({ display: "none" }),
    placeholder: (base) => ({ ...base, color: "#94a3b8", fontSize: 12, whiteSpace: "nowrap" }),
    singleValue: (base) => ({ ...base, fontSize: 12, color: "#1e293b" }),
    menu: (base) => ({ ...base, zIndex: 9999, fontSize: 12, borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.12)" }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (base, state) => ({ ...base, fontSize: 12, padding: "6px 10px", backgroundColor: state.isSelected ? "#1e293b" : state.isFocused ? "#f1f5f9" : "#fff", color: state.isSelected ? "#fff" : "#334155" }),
};

const selectStyles = {
    control: (provided) => ({
        ...provided,
        minHeight: "34px",
        height: "34px",
        fontSize: "12px"
    }),
    valueContainer: (provided) => ({
        ...provided,
        height: "34px",
        padding: "0 8px"
    }),
    indicatorsContainer: (provided) => ({
        ...provided,
        height: "34px"
    }),
    option: (provided) => ({
        ...provided,
        fontSize: "12px"
    }),
    singleValue: (provided) => ({
        ...provided,
        fontSize: "12px"
    }),
    placeholder: (provided) => ({
        ...provided,
        fontSize: "12px"
    })
};

// A date filter box that always DISPLAYS dd-mm-yyyy (regardless of the
// browser/OS locale), while still opening the browser's native calendar
// picker to pick the date. Native <input type="date"> shows/accepts only
// yyyy-mm-dd and its on-screen format follows the OS locale, which we can't
// override with CSS - so a real (but invisible) date input is layered under
// a visible dd-mm-yyyy label; clicking the label opens the native picker.
// `value`/`onChange` still work in yyyy-mm-dd (ISO), same as before.
const DateField = ({ value, onChange, min, max, style, title }) => {
    const inputRef = useRef(null);

    const displayText = (() => {
        if (!value) return "";
        const [y, m, d] = value.split("-");
        if (!y || !m || !d) return "";
        return `${d}-${m}-${y}`;
    })();

    const openPicker = () => {
        const el = inputRef.current;
        if (!el) return;
        if (typeof el.showPicker === "function") {
            try {
                el.showPicker();
                return;
            } catch (e) { /* fall through to focus */ }
        }
        el.focus();
    };

    return (
        <div style={{ position: "relative" }} title={title}>
            <div
                onClick={openPicker}
                style={{
                    ...style,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    color: value ? "#0f172a" : "#94a3b8"
                }}
            >
                <span>{displayText || "dd-mm-yyyy"}</span>
                <span style={{ fontSize: 13, opacity: 0.7 }} aria-hidden="true">📅</span>
            </div>
            <input
                ref={inputRef}
                type="date"
                value={value || ""}
                min={min || undefined}
                max={max || undefined}
                onChange={(e) => onChange(e.target.value)}
                style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    opacity: 0,
                    cursor: "pointer"
                }}
            />
        </div>
    );
};

const PhysicalInventory = () => {
    const location = useLocation();
    const [tableData, setTableData] = useState([]);

    const [showManual, setShowManual] = useState(false);
    const [manualExists, setManualExists] = useState(null);
    const MANUAL_PATH = "/assets/files/Y(Single core)_Datasheet (2).pdf";

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // ── Import (upload back the filled sheet) ──
    const fileInputRef = useRef(null);
    const [importPreview, setImportPreview] = useState(null); // response of /import/preview
    const [importFile, setImportFile] = useState(null); // the selected file, re-sent on confirm
    const [importLoading, setImportLoading] = useState(false);
    const [importConfirming, setImportConfirming] = useState(false);
    const [importModalOpen, setImportModalOpen] = useState(false);

    // ── Toast (replaces the browser alert) ──
    const [toast, setToast] = useState(null); // { message, type: "success" | "error" | "warning" }
    const toastTimer = useRef(null);

    const showToast = (message, type = "error") => {
        if (toastTimer.current) clearTimeout(toastTimer.current);
        setToast({ message, type });
        toastTimer.current = setTimeout(() => setToast(null), type === "success" ? 5000 : 7000);
    };

    useEffect(() => () => toastTimer.current && clearTimeout(toastTimer.current), []);

    const TOAST_COLORS = {
        success: { bg: "#ecfdf5", border: "#10b981", text: "#065f46", icon: "✓" },
        error:   { bg: "#fef2f2", border: "#ef4444", text: "#991b1b", icon: "✕" },
        warning: { bg: "#fffbeb", border: "#f59e0b", text: "#92400e", icon: "!" }
    };

    const [productTypes, setProductTypes] = useState([]);
    const [allProductCodeOptions, setAllProductCodeOptions] = useState([]);
    const [batchNumberOptions, setBatchNumberOptions] = useState([]);
    const [storageLocationOptions, setStorageLocationOptions] = useState([]);

    // ── Attribute filter options come from the same API Product.js uses
    // (GET /product-master/filter-options), already cascaded by the backend. ──
    const [filterOptions, setFilterOptions] = useState({
        category: [], cableType: [], code: [], color: [], noOfCores: [],
        nominalCrossSection: [], referenceOfStandard: [], voltage: [], construction: [],
        subCategory: [], qualityCategory: []
    });

    // Product code / description list for the selected Product Type (same API as Product.js)
    const [codeDescList, setCodeDescList] = useState([]);

    const [filters, setFilters] = useState({
        productType: null,
        productCode: null,
        batchNumber: null,
        storageLocation: null,
        construction: null,
        code: null,
        cableType: null,
        category: null,
        subCategory: null,
        qualityCategory: null,
        noOfCores: null,
        color: null,
        nominalCrossSection: null,
        referenceOfStandard: null,
        voltage: null,
        date: "",
        // Export-only Created Date range, sent to /physical-inventory/export
        fromDate: "",
        toDate: ""
    });

    useEffect(() => {
        loadFilterOptions();
    }, []);

    useEffect(() => {
        fetchInventory();
        setCurrentPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const loadFilterOptions = async () => {
        try {

            const [
                productTypesRes,
                productCodesRes,
                batchNumbersRes,
                storageLocationsRes
            ] = await Promise.all([
                axios.get(`${MASTER_API}/configuration/product-types`),
                axios.get(`${PURCHASE_API}/batch-list/product-codes`),
                axios.get(`${PURCHASE_API}/batch-list/batch-numbers`),
                axios.get(`${PURCHASE_API}/batch-list/storage-locations`)
            ]);

            // ── Product Type options come from the configuration master list ──
            // ── (Configuration → Master Data → Product Type) rather than distinct ──
            // ── values off batch records, so every configured type shows up and ──
            // ── inconsistent casing/duplicates from raw data are avoided. ──
            setProductTypes(
                (productTypesRes.data || []).map((r) => {
                    const val = String(r.name || r.productType || "").trim();
                    return { value: val, label: val };
                }).filter((o) => o.value !== "")
            );

            setAllProductCodeOptions(
                (productCodesRes.data.data || []).map((r) => ({
                    value: r.productCode,
                    label: r.productDescription
                        ? `${r.productCode} - ${r.productDescription}`
                        : r.productCode
                }))
            );

            setBatchNumberOptions(
                (batchNumbersRes.data.data || []).map((r) => ({
                    value: r.batch_number,
                    label: r.batch_number
                }))
            );

            setStorageLocationOptions(
                (storageLocationsRes.data.data || []).map((r) => ({
                    value: r.storage_code,
                    label: `${r.storage_code} - ${r.storage_location}`
                }))
            );

        } catch (err) {
            console.error(err);
        }
    };

    // Same API Product.js uses. The backend narrows every list by whatever is
    // already selected, and returns plain string arrays.
    const fetchFilterOptions = async () => {
        try {
            const res = await axios.get(
                `${MASTER_API}/product-master/filter-options`,
                {
                    headers: authHeaders(),
                    params: {
                        productType: filters.productType?.value || "",
                        category: filters.category?.value || "",
                        cableType: filters.cableType?.value || "",
                        code: filters.code?.value || "",
                        color: filters.color?.value || "",
                        noOfCores: filters.noOfCores?.value || "",
                        nominalCrossSection: filters.nominalCrossSection?.value || "",
                        referenceOfStandard: filters.referenceOfStandard?.value || "",
                        voltage: filters.voltage?.value || "",
                        subCategory: filters.subCategory?.value || ""
                    }
                }
            );
            setFilterOptions((prev) => ({ ...prev, ...res.data }));
        } catch (err) {
            console.error("Failed to fetch filter options", err.response?.data || err.message);
        }
    };

    useEffect(() => {
        fetchFilterOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        filters.productType,
        filters.category,
        filters.cableType,
        filters.code,
        filters.color,
        filters.noOfCores,
        filters.nominalCrossSection,
        filters.referenceOfStandard,
        filters.voltage,
        filters.subCategory
    ]);

    useEffect(() => {
        axios.get(`${MASTER_API}/product-master/productCodeDescriptionList`, {
            headers: authHeaders(),
            params: { productType: filters.productType?.value || "" }
        })
            .then((res) => setCodeDescList(res.data || []))
            .catch((err) => console.error("Failed to load product code list", err.response?.data || err.message));
    }, [filters.productType]);

    const toStrOptions = (arr) => (arr || []).map((v) => ({ value: v, label: v }));

    // Product Code options: codes that actually have stock (label "CODE - Description"),
    // narrowed to the selected Product Type.
    const typeCodeSet = new Set(codeDescList.map((i) => i.productCode));
    const cascadingProductCodeOptions = allProductCodeOptions.filter(
        (o) => !filters.productType || typeCodeSet.has(o.value)
    );

    const fetchInventory = async () => {
        try {

            const res = await axios.post(
                `${PURCHASE_API}/batch-list/list`,
                {
                    productType: filters.productType?.value || "",
                    productCode: filters.productCode?.value || "",
                    batchNumber: filters.batchNumber?.value || "",
                    storageCode: filters.storageLocation?.value || "",
                    construction: filters.construction?.value || "",
                    code: filters.code?.value || "",
                    cableType: filters.cableType?.value || "",
                    category: filters.category?.value || "",
                    subCategory: filters.subCategory?.value || "",
                    qualityCategory: filters.qualityCategory?.value || "",
                    noOfCores: filters.noOfCores?.value || "",
                    color: filters.color?.value || "",
                    nominalCrossSection: filters.nominalCrossSection?.value || "",
                    referenceOfStandard: filters.referenceOfStandard?.value || "",
                    voltage: filters.voltage?.value || "",
                    date: filters.date || ""
                }
            );

            setTableData(res.data.data || []);

        } catch (err) {
            console.error(err);
        }
    };

    // Changing an attribute filter clears the filters after it (same order and rules
    // as Product.js) so selections never contradict each other.
    const ALL_ATTRIBUTE_KEYS = [
        "cableType", "code", "color", "noOfCores", "nominalCrossSection",
        "referenceOfStandard", "voltage", "construction", "subCategory", "qualityCategory"
    ];

    const handleFilterChange = (key, opt) => {
        setFilters((prev) => {
            const updated = { ...prev, [key]: opt };
            const rawMode = (prev.productType?.value || "").trim().toLowerCase() === "raw material";
            const order = (rawMode ? RAW_MATERIAL_FILTERS : FINISHED_FILTERS).map((f) => f.key);

            if (key === "category") {
                ALL_ATTRIBUTE_KEYS.forEach((k) => { updated[k] = null; });
            } else {
                const idx = order.indexOf(key);
                if (idx !== -1) order.slice(idx + 1).forEach((k) => { updated[k] = null; });
            }
            return updated;
        });
    };

    // ── Changing Product Type resets every type-specific filter, same as
    // Product.js, because the set of visible attribute filters changes. ──
    const handleProductTypeChange = (opt) => {
        setFilters((prev) => ({
            ...prev,
            productType: opt,
            construction: null,
            code: null,
            cableType: null,
            category: null,
            subCategory: null,
            qualityCategory: null,
            noOfCores: null,
            color: null,
            nominalCrossSection: null,
            referenceOfStandard: null,
            voltage: null,
            productCode: null
        }));
    };

    // ── Which attribute filters to show for the selected Product Type ──
    const isRawMaterial =
        (filters.productType?.value || "").trim().toLowerCase() === "raw material";
    const attributeFilters = isRawMaterial ? RAW_MATERIAL_FILTERS : FINISHED_FILTERS;

    // DateField (native picker, dd-mm-yyyy display) calls onChange with the
    // yyyy-mm-dd value directly - same format filters.date/fromDate/toDate
    // are stored and sent to the backend in.
    const handleIsoDateChange = (filterKey) => (isoValue) => {
        setFilters((prev) => ({ ...prev, [filterKey]: isoValue }));
    };

    const handleDateChange = handleIsoDateChange("date");
    const handleFromDateChange = handleIsoDateChange("fromDate");
    const handleToDateChange = handleIsoDateChange("toDate");

    // All rows are shown as-is (no tab split, no merging)
    const activeData = tableData;

    // Export now comes from the backend (POST /physical-inventory/export).
    // It returns ALL active stock for the selected Product Type, with an
    // empty "Actual" column for the user to fill in.
    const downloadWorkbook = async (productType, fromDate, toDate) => {
        if (!productType) {
            showToast("Please select a Product Type to download the template", "warning");
            return;
        }

        if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
            showToast("From Date cannot be after To Date", "warning");
            return;
        }

        try {
            const res = await axios.post(
                `${PURCHASE_API}/physical-inventory/export`,
                { productType, fromDate: fromDate || "", toDate: toDate || "" },
                { responseType: "blob" }
            );

            // Use the filename sent by the server, with a fallback
            const disposition = res.headers["content-disposition"] || "";
            const match = disposition.match(/filename="?([^";]+)"?/);
            const fileName = match
                ? match[1]
                : `PhysicalInventory_${productType.replace(/\s+/g, "")}.xlsx`;

            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            // With responseType "blob", error bodies also arrive as a Blob
            let message = "Failed to export physical inventory";
            try {
                const text = await err.response?.data?.text();
                message = JSON.parse(text).message || message;
            } catch (e) { /* keep default message */ }
            showToast(message, "error");
        }
    };

    // "Export to Excel" button in the table toolbar - keeps using the page's own filter
    const handleExportExcel = () =>
        downloadWorkbook(filters.productType?.value, filters.fromDate, filters.toDate);

    // "Download Template" button inside the Import modal.
    // The template is built by the backend (POST /physical-inventory/template) so it always
    // has the current columns and the Actual Storage Code / Location dropdowns (picking one
    // fills the other). No Product Type needed.
    const handleDownloadTemplate = async () => {
        try {
            const res = await axios.post(
                `${PURCHASE_API}/physical-inventory/template`,
                {},
                { responseType: "blob" }
            );

            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;
            link.download = "PhysicalInventory_Template.xlsx";
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            // With responseType "blob", error bodies also arrive as a Blob
            let message = "Failed to download the template";
            try {
                const text = await err.response?.data?.text();
                message = JSON.parse(text).message || message;
            } catch { /* keep default message */ }
            showToast(message, "error");
        }
    };

    // Step 1: user picks the filled sheet -> backend validates it and returns
    // every line, which is shown in the confirmation modal (nothing is saved yet).
    const handleImportFileChange = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // allows picking the same file again
        if (!file) return;

        if (!/\.xlsx$/i.test(file.name)) {
            showToast("Please select the exported .xlsx file", "warning");
            return;
        }

        const formData = new FormData();
        formData.append("file", file);

        try {
            setImportLoading(true);
            const res = await axios.post(
                `${PURCHASE_API}/physical-inventory/import/preview`,
                formData,
                { headers: { "Content-Type": "multipart/form-data" } }
            );
            setImportPreview(res.data.data);
            setImportFile(file);
            setImportModalOpen(false); // hand off to the confirm/preview modal
        } catch (err) {
            showToast(err.response?.data?.message || "Failed to read the Excel file", "error");
        } finally {
            setImportLoading(false);
        }
    };

    // Step 2: user confirms in the modal -> the same file is sent again, re-validated and
    // saved in physical_inventory_import (variance lines only). Stock and the other tables are
    // updated later from the Variance screen.
    const handleImportConfirm = async () => {
        if (!importPreview || !importFile || importConfirming) return;

        const formData = new FormData();
        formData.append("file", importFile);

        try {
            setImportConfirming(true);
            const res = await axios.post(
                `${PURCHASE_API}/physical-inventory/import/confirm`,
                formData,
                { headers: { "Content-Type": "multipart/form-data" } }
            );
            const d = res.data.data;
            showToast(`Physical inventory ${d.importId} saved. ${d.linesSaved} batch(es) with variance recorded. Stock will be updated from the Variance screen.`, "success");
            setImportPreview(null);
            setImportFile(null);
            fetchInventory();
        } catch (err) {
            showToast(err.response?.data?.message || "Failed to update physical inventory", "error");
        } finally {
            setImportConfirming(false);
        }
    };

    const handleImportCancel = () => {
        setImportPreview(null);
        setImportFile(null);
    };

    const renderChange = (oldVal, newVal) =>
        oldVal === newVal || !newVal
            ? (oldVal || "-")
            : !oldVal
            ? <strong style={{ color: "#0f172a" }}>{newVal}</strong>
            : <span><span style={{ color: "#94a3b8", textDecoration: "line-through" }}>{oldVal || "-"}</span> → <strong style={{ color: "#0f172a" }}>{newVal}</strong></span>;

    const totalPages = Math.max(1, Math.ceil(activeData.length / pageSize));
    const paginatedData = activeData.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
    );

    const goToPrevPage = () => {
        setCurrentPage((prev) => Math.max(1, prev - 1));
    };

    const goToNextPage = () => {
        setCurrentPage((prev) => Math.min(totalPages, prev + 1));
    };

    const handlePageSizeChange = (e) => {
        setPageSize(Number(e.target.value));
        setCurrentPage(1);
    };

    return (
        <Layout>
            {toast && (
                <div
                    role="status"
                    style={{
                        position: "fixed",
                        top: "20px",
                        right: "20px",
                        zIndex: 100000,
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "10px",
                        maxWidth: "420px",
                        padding: "12px 14px",
                        borderRadius: "8px",
                        borderLeft: `4px solid ${TOAST_COLORS[toast.type].border}`,
                        background: TOAST_COLORS[toast.type].bg,
                        color: TOAST_COLORS[toast.type].text,
                        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.18)",
                        fontSize: "14px"
                    }}
                >
                    <span style={{ fontWeight: 700 }}>{TOAST_COLORS[toast.type].icon}</span>
                    <span style={{ flex: 1 }}>{toast.message}</span>
                    <button
                        type="button"
                        onClick={() => setToast(null)}
                        aria-label="Close"
                        style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", fontSize: "16px", lineHeight: 1 }}
                    >
                        ×
                    </button>
                </div>
            )}
            <div className="container-fluid p-4">

                <div style={{ ...styles.pageTitleBar, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div>
                        <h2 style={styles.pageTitle}>
                            Physical Inventory
                        </h2>

                        <p style={styles.pageSubtitle}>
                            View all physical inventory details
                        </p>
                    </div>

                    <button
                        onClick={() => {
                            setManualExists(null);
                            setShowManual(true);
                            fetch(MANUAL_PATH, { method: "HEAD" })
                                .then((res) => {
                                    const contentType = res.headers.get("content-type") || "";
                                    setManualExists(res.ok && contentType.includes("pdf"));
                                })
                                .catch(() => setManualExists(false));
                        }}
                        title="View User Manual"
                        style={{
                            background: "#ffffff",
                            border: "1px solid #e2e8f0",
                            borderRadius: "10px",
                            padding: "10px 14px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            cursor: "pointer",
                            color: "#3b82f6",
                            fontSize: "14px",
                            fontWeight: "600",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#3b82f6"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; }}
                    >
                        <FileText size={18} />
                        User Manual
                    </button>
                </div>

                {/* --- User Manual Popup Modal --- */}
                {showManual && (
                    <div
                        onClick={() => setShowManual(false)}
                        style={{
                            position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
                            backgroundColor: "rgba(15, 23, 42, 0.6)", display: "flex",
                            alignItems: "center", justifyContent: "center", zIndex: 1100,
                        }}
                    >
                        <div
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                background: "#ffffff", borderRadius: "12px", width: "97%", height: "96%",
                                display: "flex", flexDirection: "column", overflow: "hidden",
                                boxShadow: "0 20px 25px -5px rgba(0,0,0,0.2)",
                            }}
                        >
                            <div style={{
                                display: "flex", justifyContent: "space-between", alignItems: "center",
                                padding: "14px 20px", borderBottom: "1px solid #e2e8f0",
                            }}>
                                <h4 style={{ margin: 0, color: "#0f172a", fontSize: "16px", fontWeight: "600" }}>
                                    User Manual
                                </h4>
                                <button
                                    onClick={() => setShowManual(false)}
                                    style={{
                                        border: "none", background: "#f1f5f9", borderRadius: "8px",
                                        width: "32px", height: "32px", cursor: "pointer", fontSize: "18px",
                                        color: "#475569", lineHeight: 1,
                                    }}
                                    title="Close"
                                >
                                    ×
                                </button>
                            </div>

                            {manualExists === null && (
                                <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: "14px" }}>
                                    Checking for user manual...
                                </div>
                            )}

                            {manualExists === false && (
                                <div style={{
                                    flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
                                    justifyContent: "center", color: "#64748b", gap: "10px", padding: "20px", textAlign: "center"
                                }}>
                                    <FileText size={40} color="#cbd5e1" />
                                    <div style={{ fontSize: "15px", fontWeight: 600, color: "#334155" }}>
                                        User Manual Not Available
                                    </div>
                                    <div style={{ fontSize: "13px", color: "#94a3b8", maxWidth: "320px" }}>
                                        The user manual for this module hasn't been uploaded yet. Please check back later or contact your administrator.
                                    </div>
                                </div>
                            )}

                            {manualExists === true && (
                                <iframe
                                    src={`${MANUAL_PATH}#zoom=170`}
                                    title="User Manual PDF"
                                    style={{ width: "100%", height: "100%", border: "none" }}
                                />
                            )}
                        </div>
                    </div>
                )}

                <div style={styles.filterCard}>

                    {/* Row 1: Product Type */}
                    <div className="row mb-3">
                        <div className="col-md-3">
                            <label style={styles.compactLabel}>Product Type</label>
                            <Select
                                styles={selectStyles}
                                placeholder="Select Product Type"
                                options={productTypes}
                                value={filters.productType}
                                onChange={handleProductTypeChange}
                                isClearable
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                        </div>
                    </div>

                    {/* Row 2: type-specific attribute filters.
                        Raw Material  -> Category, Sub Category, Quality Category
                        Anything else -> Category, Cable Type, Code, Color, Cores,
                                         Cross Section, Std. Ref, Voltage, Construction */}
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 15 }}>
                        {attributeFilters.map(({ key, label }) => (
                            <div key={key} style={{ flex: "1 1 0", minWidth: 100 }}>
                                <label style={styles.compactLabel}>{label}</label>
                                <Select
                                    placeholder={label}
                                    isClearable
                                    options={toStrOptions(filterOptions[key])}
                                    value={filters[key]}
                                    onChange={(opt) => handleFilterChange(key, opt)}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={cableFilterSelectStyles}
                                />
                            </div>
                        ))}
                    </div>

                    {/* Row 3: Product Code, Batch Number, Storage Location */}
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={styles.compactLabel}>Product Code</label>
                            <Select
                                placeholder="Select..."
                                options={cascadingProductCodeOptions}
                                value={filters.productCode}
                                onChange={(val) => handleFilterChange("productCode", val)}
                                isClearable
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={cableFilterSelectStyles}
                            />
                        </div>

                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={styles.compactLabel}>Batch Number</label>
                            <Select
                                placeholder="Select..."
                                options={batchNumberOptions}
                                value={filters.batchNumber}
                                onChange={(val) => handleFilterChange("batchNumber", val)}
                                isClearable
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={cableFilterSelectStyles}
                            />
                        </div>

                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={styles.compactLabel}>Storage Location</label>
                            <Select
                                placeholder="Select..."
                                options={storageLocationOptions}
                                value={filters.storageLocation}
                                onChange={(val) => handleFilterChange("storageLocation", val)}
                                isClearable
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={cableFilterSelectStyles}
                            />
                        </div>

                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={styles.compactLabel}>Created Date</label>
                            <DateField
                                value={filters.date}
                                onChange={handleDateChange}
                                style={styles.dateInput}
                            />
                        </div>

                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={styles.compactLabel} title="Filters the Export to Excel by stock Created Date">
                                From 
                            </label>
                            <DateField
                                value={filters.fromDate}
                                onChange={handleFromDateChange}
                                max={filters.toDate}
                                style={styles.dateInput}
                            />
                        </div>

                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={styles.compactLabel} title="Filters the Export to Excel by stock Created Date">
                                To 
                            </label>
                            <DateField
                                value={filters.toDate}
                                onChange={handleToDateChange}
                                min={filters.fromDate}
                                style={styles.dateInput}
                            />
                        </div>
                    </div>

                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginBottom: "15px" }}>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx"
                        style={{ display: "none" }}
                        onChange={handleImportFileChange}
                    />
                    <button
                        onClick={() => setImportModalOpen(true)}
                        disabled={importLoading}
                        style={{ ...styles.exportBtn, background: importLoading ? "#94a3b8" : "#2563eb", cursor: importLoading ? "not-allowed" : "pointer" }}
                        title="Download a template or upload a filled sheet"
                    >
                        {importLoading ? "Reading..." : "Import from Excel"}
                    </button>
                    <button
                        onClick={handleExportExcel}
                        disabled={!filters.productType}
                        style={!filters.productType ? styles.exportBtnDisabled : styles.exportBtn}
                        title="Select a Product Type to export its active stock to Excel. Export From/To Date is optional."
                    >
                        Export to Excel
                    </button>
                </div>

                <div style={styles.tableContainer}>
                    <div className="table-responsive">
                        <table className="table" style={styles.erpTable}>
                            <thead>
                                <tr>
                                    <th style={styles.erpTh}>Product Type</th>
                                    <th style={styles.erpTh}>Product Code</th>
                                    <th style={styles.erpTh}>Description</th>
                                    <th style={styles.erpTh}>Batch Number</th>
                                    <th style={styles.erpTh}>UOM</th>
                                    <th style={styles.erpTh}>Qty</th>
                                    <th style={styles.erpTh}>Storage Location</th>
                                    <th style={styles.erpTh}>Storage Code</th>
                                    <th style={styles.erpTh}>Created Date</th>
                                    <th style={styles.erpTh}>Status</th>
                                    <th style={styles.erpTh}>Quality Status</th>
                                </tr>
                            </thead>

                            <tbody>
                                {paginatedData.length === 0 ? (
                                    <tr>
                                        <td colSpan="11" className="text-center">
                                            No Records Found
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedData.map((row) => (
                                        <tr key={row.id}>
                                            <td style={styles.erpTd}>{row.productType || "-"}</td>
                                            <td style={styles.erpTd}>
                                                <Link
                                                    to={`/cable/product/product-code?view=${encodeURIComponent(
                                                        row.product_code
                                                    )}&returnTo=${encodeURIComponent(
                                                        location.pathname + location.search
                                                    )}`}
                                                    style={styles.cellLink}
                                                >
                                                    {row.product_code}
                                                </Link>
                                            </td>
                                            <td style={styles.erpTd}>{row.description || "-"}</td>
                                            <td style={styles.erpTd}>{row.batch_number}</td>
                                            <td style={styles.erpTd}>{row.uom || "-"}</td>
                                            <td style={styles.erpTd}>{formatQty(row.qty)}</td>
                                            <td style={styles.erpTd}>{row.storage_location}</td>
                                            <td style={styles.erpTd}>{row.storage_code}</td>
                                            <td style={styles.erpTd}>{formatDate(row.created_at)}</td>
                                            <td style={styles.erpTd}>{row.status}</td>
                                            <td style={styles.erpTd}>{row.quality_status}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div style={styles.paginationBar}>
                    <div style={styles.paginationInfo}>
                        Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong> • Total {activeData.length}
                    </div>

                    <div style={styles.paginationButtons}>
                        <button
                            onClick={goToPrevPage}
                            disabled={currentPage === 1}
                            style={currentPage === 1 ? styles.paginationBtnDisabled : styles.paginationBtn}
                        >
                            Prev
                        </button>

                        <button
                            onClick={goToNextPage}
                            disabled={currentPage === totalPages}
                            style={currentPage === totalPages ? styles.paginationBtnDisabled : styles.paginationBtn}
                        >
                            Next
                        </button>

                        <select
                            value={pageSize}
                            onChange={handlePageSizeChange}
                            style={styles.pageSizeSelect}
                        >
                            {PAGE_SIZE_OPTIONS.map((size) => (
                                <option key={size} value={size}>
                                    {size} / page
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

            </div>

            {importModalOpen && (
                <div style={modalStyles.overlay}>
                    <div style={{ ...modalStyles.modal, maxWidth: 480 }}>
                        <div style={modalStyles.header}>
                            <h4 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: "#0f172a" }}>
                                Import Physical Inventory
                            </h4>
                        </div>

                        <div style={{ padding: "20px" }}>
                            <button
                                onClick={handleDownloadTemplate}
                                style={{ ...styles.exportBtn, width: "100%" }}
                            >
                                ⬇ Download Template
                            </button>

                            <div style={{ margin: "18px 0", borderTop: "1px solid #e2e8f0" }} />

                            <button
                                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                                disabled={importLoading}
                                style={{
                                    ...styles.exportBtn,
                                    background: importLoading ? "#94a3b8" : "#2563eb",
                                    cursor: importLoading ? "not-allowed" : "pointer",
                                    width: "100%"
                                }}
                            >
                                {importLoading ? "Reading..." : "⬆ Upload Filled Sheet"}
                            </button>
                            <p style={{ fontSize: 12, color: "#64748b", marginTop: 8 }}>
                                Fill in the Actual Qty column (and Actual Storage Code/Location if the
                                stock moved) on the downloaded sheet, then upload it here. To add stock
                                that isn't in the system yet, fill a new row below the data.
                            </p>
                        </div>

                        <div style={{ ...modalStyles.footer, justifyContent: "flex-end" }}>
                            <button onClick={() => setImportModalOpen(false)} style={styles.paginationBtn}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {importPreview && (
                <div style={modalStyles.overlay}>
                    <div style={modalStyles.modal}>
                        <div style={modalStyles.header}>
                            <h4 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: "#0f172a" }}>
                                Confirm Physical Inventory Import
                            </h4>
                            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                                {importPreview.fileName ? `File: ${importPreview.fileName}` : ""}
                            </div>
                        </div>

                        <div style={modalStyles.summary}>
                            <span style={modalStyles.chip}>Total rows: {importPreview.summary.totalRows}</span>
                            <span style={{ ...modalStyles.chip, background: "#fef3c7", color: "#92400e" }}>To update: {importPreview.summary.changedRows}</span>
                            <span style={{ ...modalStyles.chip, background: "#dbeafe", color: "#1e40af" }}>New stock: {importPreview.summary.newRows || 0}</span>
                            <span style={modalStyles.chip}>No change: {importPreview.summary.noChangeRows}</span>
                            <span style={{ ...modalStyles.chip, background: importPreview.summary.errorRows ? "#fee2e2" : "#f1f5f9", color: importPreview.summary.errorRows ? "#b91c1c" : "#334155" }}>Errors: {importPreview.summary.errorRows}</span>
                            <span style={{ ...modalStyles.chip, background: "#dcfce7", color: "#166534" }}>Increase: +{formatQty(importPreview.summary.totalIncrease)}</span>
                            <span style={{ ...modalStyles.chip, background: "#fee2e2", color: "#b91c1c" }}>Decrease: {formatQty(importPreview.summary.totalDecrease)}</span>
                        </div>

                        <div style={modalStyles.body}>
                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                <thead>
                                    <tr>
                                        {["S.No", "Batch Number", "Product Code", "Description", "UOM", "Qty", "Actual", "Variance", "Storage Code", "Storage Location", "Status"].map((h) => (
                                            <th key={h} style={{ ...modalStyles.th, textAlign: "left" }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {importPreview.rows.map((r, idx) => (
                                        <tr
                                            key={r.rowNo}
                                            style={{ background: r.status === "ERROR" ? "#fef2f2" : r.status === "OK" ? "#fffbeb" : "#fff" }}
                                        >
                                            <td style={modalStyles.td}>{idx + 1}</td>
                                            <td style={modalStyles.td}>{r.batchNumber}</td>
                                            <td style={modalStyles.td}>{r.productCode}</td>
                                            <td style={modalStyles.td}>{r.description || "-"}</td>
                                            <td style={modalStyles.td}>{r.uom || "-"}</td>
                                            <td style={modalStyles.td}>{r.oldQty === null ? "-" : formatQty(r.oldQty)}</td>
                                            <td style={modalStyles.td}>{r.actualQty === null ? "-" : formatQty(r.actualQty)}</td>
                                            <td style={{ ...modalStyles.td, fontWeight: 600, color: r.variance > 0 ? "#16a34a" : r.variance < 0 ? "#dc2626" : "#94a3b8" }}>
                                                {r.variance > 0 ? "+" : ""}{r.variance === 0 ? "-" : formatQty(r.variance)}
                                            </td>
                                            <td style={modalStyles.td}>{renderChange(r.oldStorageCode, r.newStorageCode)}</td>
                                            <td style={modalStyles.td}>{renderChange(r.oldStorageLocation, r.newStorageLocation)}</td>
                                            <td style={{ ...modalStyles.td, color: r.status === "ERROR" ? "#b91c1c" : "#334155" }}>
                                                {r.status === "ERROR" && <strong>{r.message}</strong>}
                                                {r.status === "OK" && (
                                                    <span>
                                                        {r.changeType === "NEW"
                                                            ? `New stock (${r.newStatus})`
                                                            : r.changeType === "QTY" ? "Qty update" : r.changeType === "STORAGE" ? "Storage update" : "Qty + Storage update"}
                                                        {r.message ? <span style={{ color: "#b45309" }}> ⚠ {r.message}</span> : null}
                                                    </span>
                                                )}
                                                {r.status === "NO_CHANGE" && "No change"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div style={modalStyles.footer}>
                            <div style={{ fontSize: 12, color: importPreview.canConfirm ? "#64748b" : "#b91c1c" }}>
                                {importPreview.summary.errorRows > 0
                                    ? "Fix the highlighted errors in the Excel sheet and import again."
                                    : !importPreview.canConfirm
                                        ? "No variance found in this sheet. Nothing to save."
                                        : "Confirming will save only the batches with a variance. Stock is not updated yet - update it from the Variance screen."}
                            </div>
                            <div style={{ display: "flex", gap: 10 }}>
                                <button onClick={handleImportCancel} disabled={importConfirming} style={styles.paginationBtn}>
                                    Cancel
                                </button>
                                <button
                                    onClick={handleImportConfirm}
                                    disabled={!importPreview.canConfirm || importConfirming}
                                    style={!importPreview.canConfirm || importConfirming ? styles.exportBtnDisabled : styles.exportBtn}
                                >
                                    {importConfirming ? "Updating..." : "Confirm & Update"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default PhysicalInventory;